import { SHOPIFY_STOREFRONT_GRAPHQL_API, SHOPIFY_STOREFRONT_TOKEN } from "@/config";
import { GET_SHOP_METAFIELD } from "@/lib/shopify/queries";
import { TDayTypeInterval } from "@/types";

// The booking component only serves Sydney today. The metafield is keyed per
// location, so supporting another venue means passing its id here.
const DEFAULT_LOCATION_ID = "sydney";

const isDayTypeInterval = (entry: unknown): entry is TDayTypeInterval => {
  if (!entry || typeof entry !== "object") return false;
  const { start, end, type } = entry as Record<string, unknown>;
  return typeof start === "string" && typeof end === "string" && typeof type === "string";
};

/** Parses the metafield's JSON value, keeping only well-formed entries. */
export const parseDayTypeIntervals = (value: string | null | undefined): TDayTypeInterval[] => {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(isDayTypeInterval) : [];
  } catch {
    return [];
  }
};

/**
 * Loads the location's holiday ranges from the shop metafield
 * `hk-app.day-type-intervals-{locationId}`. Resolves to [] on any failure so
 * the calendar falls back to weekend-only Sprint days rather than breaking.
 */
export const fetchDayTypeIntervals = async (locationId: string = DEFAULT_LOCATION_ID): Promise<TDayTypeInterval[]> => {
  try {
    const response = await fetch(SHOPIFY_STOREFRONT_GRAPHQL_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
      },
      body: JSON.stringify({
        query: GET_SHOP_METAFIELD,
        variables: { namespace: "hk-app", key: `day-type-intervals-${locationId}` },
      }),
    });
    const data = await response.json();
    return parseDayTypeIntervals(data?.data?.shop?.metafield?.value);
  } catch (error) {
    console.error("Failed to fetch day type intervals:", error);
    return [];
  }
};
